import { useCallback, useState } from "react";
import {
  ExpandedState,
  OnChangeFn,
  SortingState,
  Updater,
} from "@tanstack/react-table";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { QUERY_PARAMS_KEYS } from "consts";
import { ParsedQueryParams } from "types";
import { getParsedQueryParams } from "utils";

import {
  DEFAULT_INITIAL_SORTING,
  SORT_DIRECTIONS,
} from "./consts";
import {
  getInitialSortingState,
  rowIdsToExpandedState,
} from "./utils";

export const useTableExpandedRows = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const expandedRowsIds = searchParams.getAll(QUERY_PARAMS_KEYS.EXPANDED_ROWS);

  const [expanded, setExpanded] = useState<ExpandedState>(() =>
    rowIdsToExpandedState(expandedRowsIds),
  );

  const setInitialExpanded = useCallback(() => setExpanded({}), []);
  const updateQueryParamsWithInitialExpanded = useCallback(
    (queryParams: ParsedQueryParams) => {
      const newQueryParams = { ...queryParams };
      delete newQueryParams[QUERY_PARAMS_KEYS.EXPANDED_ROWS];

      return newQueryParams;
    },
    [],
  );

  const onExpandedChange = useCallback(
    (updaterFn: Updater<ExpandedState>) => {
      setExpanded((prev) => {
        const newExpanded = (
          updaterFn as (old: ExpandedState) => ExpandedState
        )(prev);

        setSearchParams(
          createSearchParams({
            ...getParsedQueryParams(searchParams),
            [QUERY_PARAMS_KEYS.EXPANDED_ROWS]: Object.keys(newExpanded),
          }),
        );

        return newExpanded;
      });
    },
    [searchParams, setSearchParams],
  );

  return {
    expanded,
    onExpandedChange,
    setInitialExpanded,
    updateQueryParamsWithInitialExpanded,
  };
};

export const useTableSorting = (
  tableColumnToSortColumnMap: Record<string, string>,
  initialSortingState: SortingState = DEFAULT_INITIAL_SORTING,
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [sorting, setSorting] = useState<SortingState>(() =>
    getInitialSortingState(
      searchParams,
      tableColumnToSortColumnMap,
      initialSortingState,
    ),
  );

  const isSorting = !!sorting[0];
  const sortBy = isSorting
    ? tableColumnToSortColumnMap[sorting[0].id]
    : undefined;
  const sortDirection = isSorting
    ? sorting[0].desc
      ? SORT_DIRECTIONS.DESC
      : SORT_DIRECTIONS.ASC
    : undefined;

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterFn) => {
      setSorting((prev) => {
        const newSorting = (updaterFn as (old: SortingState) => SortingState)(
          prev,
        );

        const isNewSorting = newSorting[0];
        const newSortBy = isNewSorting ? newSorting[0].id : undefined;
        const newSortDirection = isNewSorting
          ? newSorting[0].desc
            ? SORT_DIRECTIONS.DESC
            : SORT_DIRECTIONS.ASC
          : undefined;

        const queryParams = getParsedQueryParams(searchParams);

        if (
          !newSortBy ||
          !newSortDirection ||
          (newSortBy === initialSortingState[0]?.id &&
            initialSortingState[0]?.desc === newSorting[0]?.desc)
        ) {
          delete queryParams[QUERY_PARAMS_KEYS.SORT_BY];
          delete queryParams[QUERY_PARAMS_KEYS.SORT_DIRECTION];
        } else {
          queryParams[QUERY_PARAMS_KEYS.SORT_BY] = newSortBy;
          queryParams[QUERY_PARAMS_KEYS.SORT_DIRECTION] = newSortDirection;
        }

        setSearchParams(createSearchParams(queryParams));

        return newSorting;
      });
    },
    [searchParams, setSearchParams, initialSortingState],
  );

  return {
    sorting,
    onSortingChange,
    sortBy,
    sortDirection,
  };
};